/* The assistant inside its window (app#397): a header with the window's name, the conversation
   scrolling in a box of its own, and the field at the bottom. The window around it — the phone's
   sheet or the laptop's panel — gives the height (`AssistantWindow.module.css`); here the header and
   the field keep theirs and the conversation takes the rest. Tokens only — the brand's surfaces and
   lines; the accent sits on the actions (Send, Book these dates, Prepare the offer). */

.chat {
  display: flex;
  flex-direction: column;
  gap: var(--gac-space-sm);
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  gap: var(--gac-space-xs);
  flex: 0 0 auto;
}

.title {
  flex: 1 1 auto;
  margin: 0;
  font-family: var(--gac-font-text);
  font-size: 17px;
  line-height: 22px;
  font-weight: 600;
  color: var(--gac-text-primary);
}

/* "New conversation": a quiet text action of the header, a whole touch target high. */
.textButton {
  min-height: var(--gac-control-touch-target);
  padding: 0 var(--gac-space-xs);
  border: none;
  border-radius: var(--gac-radius-sm);
  background: transparent;
  color: var(--gac-text-secondary);
  font-family: var(--gac-font-text);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.textButton:hover:not(:disabled) {
  color: var(--gac-text-primary);
}

.textButton:disabled {
  color: var(--gac-text-disabled);
  cursor: default;
}

.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--gac-control-touch-target);
  height: var(--gac-control-touch-target);
  border: 1px solid var(--gac-line-border);
  border-radius: var(--gac-radius-pill);
  background: var(--gac-surface-overlay);
  color: var(--gac-text-primary);
  cursor: pointer;
}

.textButton:focus-visible,
.close:focus-visible,
.example:focus-visible,
.send:focus-visible {
  outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
  outline-offset: 2px;
}

/* The conversation's box: it takes what the header and the field leave, and scrolls. A drag belongs
   to it only while part of the conversation is out of sight (`data-more`, set by the chat): a box
   that scrolls by its style opens the gestures anew under the dialog's `touch-action: none`, and
   with nothing to scroll a drag went on to the page behind the sheet (app#292, the Telegram Mini
   App on an iPhone). `position: relative` makes it the lines' offset parent (scrolling to a line). */
.scroll {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: none;
  display: flex;
  flex-direction: column;
  gap: var(--gac-space-sm);
}

.scroll[data-more] {
  touch-action: pan-y;
}

/* Two requests a person may start from: a tap puts one into the field. */
.examples {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gac-space-xs);
}

.example {
  min-height: var(--gac-control-touch-target);
  padding: 0 var(--gac-space-sm);
  border: 1px solid var(--gac-line-border);
  border-radius: var(--gac-radius-pill);
  background: var(--gac-surface-raised);
  color: var(--gac-text-primary);
  font-family: var(--gac-font-text);
  font-size: 14px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}

.example:hover {
  background: var(--gac-surface-hover);
}

/* The field and the round Send beside it, at the bottom of the window. */
.form {
  display: flex;
  align-items: flex-end;
  gap: var(--gac-space-xs);
  flex: 0 0 auto;
  width: 100%;
}

.field {
  flex: 1 1 auto;
  min-width: 0;
  min-height: var(--gac-control-touch-target);
  max-height: 132px;
  padding: 10px 14px;
  border-radius: 22px;
  border: 1px solid var(--gac-line-border);
  background: var(--gac-surface-base);
  color: var(--gac-text-primary);
  font-family: inherit;
  /* 16px, not less: below 16 iOS Safari zooms the page into a focused field. */
  font-size: 16px;
  line-height: 22px;
  /* The field grows with the words, up to five lines, then scrolls. */
  field-sizing: content;
  resize: none;
  box-sizing: border-box;
  outline: none;
}

.field::placeholder {
  color: var(--gac-text-muted);
}

.field:focus {
  border-color: var(--gac-accent);
}

.field:disabled {
  opacity: 0.6;
}

/* Send: a small round accent button with a black mark. With nothing to send it is a quiet raised
   circle, not a dimmed accent (the olive bar of the old panel, design review 2026-11-03). */
.send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--gac-control-touch-target);
  height: var(--gac-control-touch-target);
  border: none;
  border-radius: var(--gac-radius-pill);
  background: var(--gac-accent);
  color: var(--gac-accent-ink);
  cursor: pointer;
}

.send:hover:not(:disabled) {
  background: var(--gac-accent-hover);
}

.send:disabled {
  background: var(--gac-surface-overlay);
  color: var(--gac-text-disabled);
  cursor: default;
}

.wide {
  width: 100%;
  flex: 0 0 auto;
}

.log {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.entry {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
}

.bubble {
  padding: 10px 14px;
  border-radius: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.person .bubble {
  align-self: flex-end;
  max-width: 85%;
  background: var(--gac-surface-overlay);
}

.assistant .bubble {
  align-self: flex-start;
  max-width: 100%;
  background: var(--gac-surface-base);
  border: 1px solid var(--gac-line-divider);
}

.picks {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pick {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* The catalog's card is as tall as its grid row (`height: 100%` in ListingCard). Here the row is the
   whole pick — the card, the reason and the button — so that percentage would make the card the
   height of the pick and push the reason and the button out, under the field (SF-N3 caught it: the
   button could not be clicked). The card takes what the reason and the button leave, which also
   lines the buttons of a row up at the bottom. `.pick > .card` outranks the component's own rule. */
.pick > .card {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}

.reason {
  padding: 0 4px;
}

/* The line under a rental's price (app#362): what the booking's band saves against 1–3 days. It
   sits in the card's price row, whose number is set in the display face on one line; the line is
   text, wraps on a phone, and reads like the card's other text — the secondary grey, the caption
   step — with its two savings in white, as the card sets its price: a grey label, a white figure
   (Tim, 2026-10-31, variant B of four: the green status tone read as a system notice, not as part
   of the card). */
.saving {
  display: block;
  margin-top: 2px;
  font-family: var(--gac-font-text);
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  white-space: normal;
  color: var(--gac-text-secondary);
}

.savingFigure {
  font-weight: 600;
  color: var(--gac-text-primary);
}

.book {
  width: 100%;
}

/* The brand's error box, as the rental page draws it: a base surface with the danger line. */
.failure {
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--gac-surface-base);
  border: 1px solid var(--gac-status-danger);
}

/* The evidence card under a car for sale (api#425): four short rows — label, then the platform's
   sentence — and the way to the dealer's page. A row's tone says what the evidence means at a
   glance: a VIN that matches is the success tone, a missing one the danger tone, a decoder that
   could not answer the muted text; the rest is plain. Tokens only, as everywhere on the panel. */

.evidence {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--gac-surface-base);
  border: 1px solid var(--gac-line-divider);
}

.evidenceRow {
  display: grid;
  grid-template-columns: 7.5em minmax(0, 1fr);
  gap: 8px;
  align-items: baseline;
}

.evidenceRow dt,
.evidenceRow dd {
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.evidenceRow dt {
  border-left: 3px solid transparent;
  padding-left: 8px;
}

.tone-good dt {
  border-left-color: var(--gac-status-success);
}

.tone-bad dt {
  border-left-color: var(--gac-status-danger);
}

.tone-muted dt {
  border-left-color: var(--gac-text-muted);
}

/* The words dim, never the link beside them (the Dealer row: a record that could not be read). */
.tone-muted dd > :not(.dealerLink) {
  opacity: 0.8;
}

/* The dealer's record (api#434) sits above the link to the dealer's cars, in the same cell. */
.dealerRecord {
  display: block;
  margin-bottom: 4px;
}

.dealerLink {
  text-decoration: underline;
  text-underline-offset: 2px;
  color: inherit;
}

.dealerLink:focus-visible {
  outline: 2px solid var(--gac-accent);
  outline-offset: 2px;
  border-radius: 4px;
}

/* The "Check this car" list under a car for sale (api#430): the same surface as the evidence card,
   links to the providers' and the officials' own sites, one per line. */
.checks {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--gac-surface-base);
  border: 1px solid var(--gac-line-divider);
  overflow-wrap: anywhere;
}

.checksTitle {
  margin: 0;
}

.checkList {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0 0 0 16px;
}

